import axios from "axios";
import { toast } from "sonner";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Evita di gestire il blocco accesso più volte per richieste in parallelo
let bloccoAccessoInCorso = false;

/** Secondi di attesa comunicati dal server su un 429 (header Retry-After). */
export function secondiAttesa(response) {
  const raw = response?.headers?.["retry-after"] ?? response?.headers?.["Retry-After"];
  const n = parseInt(raw, 10);
  if (!isNaN(n) && n > 0) return n;
  const reset = parseInt(response?.headers?.["x-ratelimit-reset"], 10);
  if (!isNaN(reset)) return Math.max(1, reset - Math.floor(Date.now() / 1000));
  return 60;
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    // 429: nessun ritentativo automatico. Il messaggio del server contiene già
    // il tempo di attesa; le pagine che hanno un pulsante di invio lo leggono
    // da retryAfter (secondi) per disabilitarlo fino alla scadenza.
    if (status === 429) {
      err.retryAfter = secondiAttesa(err.response);
      const path429 = window.location.pathname;
      // Sul login il messaggio è mostrato inline dal form, senza toast doppio
      if (!path429.startsWith("/login") && !path429.startsWith("/iscrizione") && !path429.startsWith("/registrazione")) {
        toast.error(errMsg(err, "Troppi tentativi, riprova tra poco"));
      }
    }
    const path = window.location.pathname;

    // Accesso animatori/aiuti sospeso dai responsabili: disconnetti e porta al
    // login con l'avviso. Riconosciuto dal codice nel dettaglio (non un 403 qualsiasi).
    const detail = err.response?.data?.detail;
    const accessoBloccato = status === 403 && detail && typeof detail === "object" && detail.code === "accesso_staff_bloccato";
    // Sul form di login lasciamo propagare l'errore (mostrato inline); altrove
    // significa che l'utente è già dentro e va disconnesso.
    if (accessoBloccato && !path.startsWith("/login")) {
      if (!bloccoAccessoInCorso) {
        bloccoAccessoInCorso = true;
        try { sessionStorage.setItem("avviso_accesso_staff", detail.message || "L'accesso all'app è stato sospeso dai Responsabili."); } catch { /* noop */ }
        // Chiude la sessione lato server, poi porta al login (reload completo)
        api.post("/auth/logout").catch(() => {}).finally(() => { window.location.href = "/login"; });
      }
      return new Promise(() => {});
    }

    if (status === 401 && !path.startsWith("/login") && !path.startsWith("/iscrizione") && !path.startsWith("/registrazione")) {
      window.location.href = "/login";
      return new Promise(() => {});
    }
    return Promise.reject(err);
  }
);

window.addEventListener("unhandledrejection", (e) => {
  if (e.reason?.isAxiosError) {
    e.preventDefault();
  }
});

export function errMsg(err, fallback = "Qualcosa è andato storto") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(". ") || fallback;
  if (d && typeof d === "object" && d.message) return d.message;
  return fallback;
}

export default api;
